import { create } from 'zustand';

interface PinchAnimationState {
  pinchState: Record<
    string,
    {
      hasPinchAnimationTimedOut: boolean;
      hasTriedPinchingAsset: boolean;
      timerStarted: boolean;
    }
  >;
  setHasTriedPinchingAsset: (uniqueKey: string) => void;
  initPinchAnimationState: (uniqueKey: string) => void;
  startTimerIfNeeded: (uniqueKey: string) => void;
  getPinchState: (uniqueKey: string) => {
    hasPinchAnimationTimedOut: boolean;
    hasTriedPinchingAsset: boolean;
  };
}

export const usePinchAnimationStore = create<PinchAnimationState>()((set, get) => {
  const timers: Record<string, NodeJS.Timeout | null> = {};

  const startTimer = (uniqueKey: string) => {
    // Clear any existing timer for this uniqueKey
    if (timers[uniqueKey]) {
      clearTimeout(timers[uniqueKey]);
    }
    // Set new timer
    timers[uniqueKey] = setTimeout(() => {
      set((state) => ({
        pinchState: {
          ...state.pinchState,
          [uniqueKey]: {
            ...state.pinchState[uniqueKey],
            hasPinchAnimationTimedOut: true,
          },
        },
      }));
      // Persist timeout state so animation doesn't show again this session
      localStorage.setItem(`pinchingAssetTimeout:${uniqueKey}`, 'true');
    }, 3000);
  };

  return {
    pinchState: {},

    setHasTriedPinchingAsset: (uniqueKey: string) => {
      set((state) => ({
        pinchState: {
          ...state.pinchState,
          [uniqueKey]: {
            ...state.pinchState[uniqueKey],
            hasTriedPinchingAsset: true,
          },
        },
      }));
      localStorage.setItem(`pinchingAsset:${uniqueKey}`, 'true');
    },

    // Called when meeting/masterclass loads - just initializes state from localStorage, no timer
    initPinchAnimationState: (uniqueKey: string) => {
      const hasTriedPinching = !!localStorage.getItem(`pinchingAsset:${uniqueKey}`);
      const hasTimedOut = !!localStorage.getItem(`pinchingAssetTimeout:${uniqueKey}`);

      set((state) => ({
        pinchState: {
          ...state.pinchState,
          [uniqueKey]: {
            hasTriedPinchingAsset: hasTriedPinching,
            hasPinchAnimationTimedOut: hasTimedOut,
            timerStarted: false,
          },
        },
      }));
    },

    // Called when AssetModal opens - starts timer only once per session
    startTimerIfNeeded: (uniqueKey: string) => {
      const currentState = get().pinchState[uniqueKey];

      // Don't start timer if already dismissed or timer already started
      if (
        currentState?.hasTriedPinchingAsset ||
        currentState?.hasPinchAnimationTimedOut ||
        currentState?.timerStarted
      ) {
        return;
      }

      set((state) => ({
        pinchState: {
          ...state.pinchState,
          [uniqueKey]: {
            ...state.pinchState[uniqueKey],
            timerStarted: true,
          },
        },
      }));
      startTimer(uniqueKey);
    },

    getPinchState: (uniqueKey: string) => {
      const state = get().pinchState[uniqueKey] || {
        hasPinchAnimationTimedOut: false,
        hasTriedPinchingAsset: false,
      };
      return {
        hasPinchAnimationTimedOut: state.hasPinchAnimationTimedOut,
        hasTriedPinchingAsset: state.hasTriedPinchingAsset,
      };
    },
  };
});
